import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Activity, ShieldAlert, BarChart3, ArrowRight, CheckCircle2, XCircle } from 'lucide-react';

interface GateProps {
  title: string;
  status: 'passed' | 'blocked' | 'warning';
  icon: React.ReactNode;
  description: string;
  metrics?: { label: string; value: string }[];
}

const Gate: React.FC<GateProps> = ({ title, status, icon, description, metrics }) => {
  const getStatusColor = () => {
    switch (status) {
      case 'passed': return 'border-emerald-500/50 bg-emerald-500/10 text-emerald-500';
      case 'blocked': return 'border-rose-500/50 bg-rose-500/10 text-rose-500';
      case 'warning': return 'border-amber-500/50 bg-amber-500/10 text-amber-500';
      default: return 'border-slate-500/50 bg-slate-500/10 text-slate-500';
    }
  };

  const getStatusIcon = () => {
    switch (status) {
      case 'passed': return <CheckCircle2 className="w-5 h-5 text-emerald-500" />;
      case 'blocked': return <XCircle className="w-5 h-5 text-rose-500" />;
      case 'warning': return <ShieldAlert className="w-5 h-5 text-amber-500" />;
      default: return null;
    }
  };

  return (
    <div className={`relative p-4 rounded-lg border ${getStatusColor()} transition-all`}>
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-md bg-background/50 backdrop-blur-sm">
            {icon}
          </div>
          <div>
            <h4 className="font-semibold">{title}</h4>
            <p className="text-sm opacity-80 mt-1">{description}</p>
          </div>
        </div>
        <div>{getStatusIcon()}</div>
      </div>
      
      {metrics && metrics.length > 0 && (
        <div className="grid grid-cols-2 gap-2 mt-4 pt-4 border-t border-current/20">
          {metrics.map((m, i) => (
            <div key={i} className="flex flex-col">
              <span className="text-xs opacity-70">{m.label}</span>
              <span className="font-medium">{m.value}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const GateWaterfall: React.FC<{
  dataHealth: any;
  regime: any;
  circuitBreaker: any;
  universe: any;
}> = ({ dataHealth, regime, circuitBreaker, universe }) => {
  // Derive status from props (mocked logic for now)
  const isDataOk = dataHealth?.status === 'healthy';
  const isRegimeOk = regime?.regime !== 'bearish' && regime?.regime !== 'high_volatility';
  const isBreakerOk = circuitBreaker?.status === 'normal';
  
  return (
    <Card className="col-span-full xl:col-span-4 h-full">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Activity className="w-5 h-5 text-primary" />
          Gate Waterfall Observer
        </CardTitle>
        <p className="text-sm text-muted-foreground">Real-time visualization of execution blocks.</p>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col gap-3">
          
          <Gate 
            title="1. Data & API Health"
            status={isDataOk ? 'passed' : 'warning'}
            icon={<Activity className="w-5 h-5" />}
            description={isDataOk ? "All websockets connected and data fresh." : "Experiencing API latency or disconnects."}
            metrics={[
              { label: "Quote Latency", value: "< 50ms" },
              { label: "API Quota", value: "92% remaining" }
            ]}
          />
          
          <div className="flex justify-center -my-1">
            <ArrowRight className="w-5 h-5 text-muted-foreground rotate-90" />
          </div>

          <Gate 
            title="2. Market Regime Observer"
            status={isRegimeOk ? 'passed' : 'blocked'}
            icon={<BarChart3 className="w-5 h-5" />}
            description={`Current Regime: ${regime?.regime || 'Unknown'}`}
            metrics={[
              { label: "VIX", value: regime?.vixLevel?.toFixed(2) || '--' },
              { label: "SPY Trend", value: regime?.spyPrice > regime?.spyMa50 ? 'Bullish' : 'Bearish' }
            ]}
          />

          <div className="flex justify-center -my-1">
            <ArrowRight className="w-5 h-5 text-muted-foreground rotate-90" />
          </div>

          <Gate 
            title="3. Circuit Breaker Limits"
            status={isBreakerOk ? 'passed' : 'warning'}
            icon={<ShieldAlert className="w-5 h-5" />}
            description={isBreakerOk ? "Risk exposure within normal bounds." : "Drawdown limit reached. No new entries."}
            metrics={[
              { label: "Daily Drawdown", value: "-0.4%" },
              { label: "Max Exposure", value: "35%" }
            ]}
          />

          <div className="flex justify-center -my-1">
            <ArrowRight className="w-5 h-5 text-muted-foreground rotate-90" />
          </div>

          <Gate 
            title="4. Signal Generation"
            status="passed"
            icon={<Activity className="w-5 h-5" />}
            description="Active pattern matching across hydrated universes."
            metrics={[
              { label: "Universe Size", value: `${universe?.totalSymbols || 0} Assets` },
              { label: "Active Patterns", value: "12" }
            ]}
          />
        </div>
      </CardContent>
    </Card>
  );
};
